import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  FolderOpen,
  HardDrive,
  Link2,
  Link2Off,
  Loader2,
  LucideIcon,
  Pencil,
  RefreshCw,
  ShoppingCart,
  ToggleLeft,
  ToggleRight,
  Plug,
  AlertTriangle,
  X,
} from "lucide-react";
import { useBusiness } from "../../contexts/BusinessContext";
import {
  fetchStorageIntegrations,
  connectStorageIntegration,
  updateStorageIntegration,
  disconnectStorageIntegration,
  getDropboxAuthUrl,
  getGoogleAuthUrl,
  getOneDriveAuthUrl,
  listDropboxFolders,
  listGoogleFolders,
  listOneDriveFolders,
  StorageIntegration,
  StorageProvider,
  ExportTemplate,
  DateSortStructure,
  StorageFolder,
} from "../../services/apiService";

const NAVY = "#2a2420";
const ORANGE = "#607840";
const SURFACE = "#fff";
const RAISED = "#fff";
const CANVAS = "#fff";
const LINE = "#e9e1d4";
const MUTED = "#8a8074";
const CHIP = "#f3ece1";
const SHADOW = "0 1px 3px rgba(42,36,32,.08), 0 12px 28px rgba(42,36,32,.06)";
const ACCENT_WEAK = "rgba(96,120,64,0.12)";
const ACCENT_LINE = "rgba(96,120,64,0.30)";
const ACCENT_SHADOW = "0 2px 8px rgba(96,120,64,0.25)";
const UP = "#c2412e";
const UP_WEAK = "rgba(194,65,46,0.12)";
const UP_LINE = "rgba(194,65,46,0.24)";
const DOWN = "#3c7d57";
const DOWN_WEAK = "rgba(60,125,87,0.12)";
const DOWN_LINE = "rgba(60,125,87,0.24)";
const BORDER = LINE;

// ── Provider metadata ──────────────────────────────────────────────

interface ProviderMeta {
  id: StorageProvider;
  name: string;
  description: string;
  color: string;
  bgColor: string;
  icon: React.FC<{ size?: number; className?: string; style?: React.CSSProperties }>;
  comingSoon?: boolean;
}

function GoogleDriveIcon({ size = 20, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 87.3 78" style={style}>
      <path d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3L27.5 53H0c0 1.55.4 3.1 1.2 4.5z" fill="#0066da" />
      <path d="M43.65 25L29.9 1.2C28.55 2 27.4 3.1 26.6 4.5L1.2 48.5C.4 49.9 0 51.45 0 53h27.5z" fill="#00ac47" />
      <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5H59.8l5.85 11.5z" fill="#ea4335" />
      <path d="M43.65 25L57.4 1.2C56.05.4 54.5 0 52.9 0H34.4c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d" />
      <path d="M59.8 53H27.5L13.75 76.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc" />
      <path d="M73.4 26.5L60.7 4.5C59.9 3.1 58.75 2 57.4 1.2L43.65 25 59.8 53h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00" />
    </svg>
  );
}

function DropboxIcon({ size = 20, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={style}>
      <path d="M6 3L12 7.2L6 11.4L0 7.2L6 3Z" fill="#0061FF" />
      <path d="M18 3L24 7.2L18 11.4L12 7.2L18 3Z" fill="#0061FF" />
      <path d="M0 13.8L6 9.6L12 13.8L6 18L0 13.8Z" fill="#0061FF" />
      <path d="M24 13.8L18 18L12 13.8L18 9.6L24 13.8Z" fill="#0061FF" />
      <path d="M6 19.2L12 15L18 19.2L12 23.4L6 19.2Z" fill="#0061FF" />
    </svg>
  );
}

function OneDriveIcon({ size = 20, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={style}>
      <path d="M20.5 11.5C20.5 9.57 19.1 7.96 17.25 7.63C16.6 5.54 14.68 4 12.38 4C9.86 4 7.8 5.93 7.55 8.38C5.56 8.79 4.07 10.58 4 12.7V13C4 15.21 5.79 17 8 17H20C21.66 17 23 15.66 23 14C23 12.49 21.89 11.24 20.5 11.5Z" fill="#0078D4" />
      <path d="M7 16.5C5.07 16.5 3.5 14.93 3.5 13C3.5 11.2 4.86 9.71 6.62 9.52C6.98 7.03 9.13 5.13 11.72 5.01C10.31 5.37 9.13 6.39 8.55 7.75C6.63 7.9 5.12 9.5 5 11.44C3.82 11.76 3 12.83 3 14.08C3 15.66 4.34 17 5.92 17H8C7.64 16.87 7.31 16.7 7 16.5Z" fill="#28A8E0" />
    </svg>
  );
}

const PROVIDERS: ProviderMeta[] = [
  {
    id: "google_drive",
    name: "Google Drive",
    description: "Export invoice files to your Google Drive account",
    color: "#0F9D58",
    bgColor: "rgba(15,157,88,0.08)",
    icon: GoogleDriveIcon,
  },
  {
    id: "dropbox",
    name: "Dropbox",
    description: "Sync invoice files to your Dropbox storage",
    color: "#0061FF",
    bgColor: "rgba(0,97,255,0.08)",
    icon: DropboxIcon,
  },
  {
    id: "onedrive",
    name: "OneDrive",
    description: "Microsoft OneDrive export is coming soon",
    color: "#0078D4",
    bgColor: "rgba(0,120,212,0.08)",
    icon: OneDriveIcon,
    comingSoon: true,
  },
];

function getProvider(id: StorageProvider): ProviderMeta {
  return PROVIDERS.find((p) => p.id === id) ?? PROVIDERS[0];
}

// ── Folder structure preview ───────────────────────────────────────

function FolderPreview({
  template,
  dateSortStructure,
  rootFolderName,
}: {
  template: ExportTemplate;
  dateSortStructure: DateSortStructure;
  rootFolderName: string;
}) {
  // rootFolderName may be a full path like "/Bill Lens Invoices" — show just the last segment
  const raw = rootFolderName.trim();
  const root = raw
    ? raw.split("/").filter(Boolean).pop() ?? raw
    : "Bill Lens Invoices";

  const parts = new Set(dateSortStructure.split("_"));
  const hasYear  = parts.has("year");
  const hasMonth = parts.has("month");
  const hasWeek  = parts.has("week");
  const hasDay   = parts.has("day");

  type Line = { depth: number; name: string; isFolder: boolean };
  const lines: Line[] = [];

  if (template === "single_folder") {
    lines.push(
      { depth: 0, name: root, isFolder: true },
      { depth: 1, name: "invoice_ABC123.pdf", isFolder: false },
      { depth: 1, name: "invoice_DEF456.pdf", isFolder: false },
      { depth: 1, name: "invoice_GHI789.pdf", isFolder: false },
    );
  } else {
    lines.push({ depth: 0, name: root, isFolder: true });
    let d = 1;
    if (hasYear)  lines.push({ depth: d++, name: "2026", isFolder: true });
    if (hasMonth) lines.push({ depth: d++, name: "04 - April", isFolder: true });
    if (hasWeek)  lines.push({ depth: d++, name: hasMonth ? "Week 2" : "Week 14", isFolder: true });
    if (hasDay)   lines.push({ depth: d++, name: "29", isFolder: true });
    lines.push({ depth: d, name: "invoice_ABC123.pdf", isFolder: false });
    lines.push({ depth: d, name: "invoice_DEF456.pdf", isFolder: false });

    // second example with an older date
    let d2 = 1;
    if (hasYear)  lines.push({ depth: d2++, name: "2025", isFolder: true });
    if (hasMonth) lines.push({ depth: d2++, name: "12 - December", isFolder: true });
    if (hasWeek)  lines.push({ depth: d2++, name: hasMonth ? "Week 3" : "Week 51", isFolder: true });
    if (hasDay)   lines.push({ depth: d2++, name: "15", isFolder: true });
    lines.push({ depth: d2, name: "invoice_GHI789.pdf", isFolder: false });
  }

  return (
    <div
      className="rounded-lg px-4 py-3 text-[11px] font-mono"
      style={{
        backgroundColor: `${NAVY}06`,
        border: `1px solid ${NAVY}12`,
      }}
    >
      <p className="text-[10px] font-sans font-semibold uppercase tracking-widest mb-2" style={{ color: NAVY, opacity: 0.4 }}>
        Folder preview
      </p>
      {lines.map((line, i) => (
        <div key={i} className="flex items-center gap-1" style={{ paddingLeft: `${line.depth * 14}px`, marginBottom: "2px" }}>
          {line.isFolder ? (
            <FolderOpen size={11} style={{ color: ORANGE, flexShrink: 0 }} />
          ) : (
            <HardDrive size={10} style={{ color: NAVY, opacity: 0.35, flexShrink: 0 }} />
          )}
          <span style={{ color: line.isFolder ? NAVY : `${NAVY}88` }}>{line.name}</span>
        </div>
      ))}
    </div>
  );
}

// ── Folder picker field ────────────────────────────────────────────

function FolderPickerField({
  provider,
  businessId,
  displayName,
  onChange,
}: {
  provider: StorageProvider;
  businessId?: string;
  displayName: string;       // current display value shown in the field
  onChange: (name: string, folderId?: string) => void;
}) {
  const isGoogle = provider === "google_drive";
  const isOneDrive = provider === "onedrive";
  const usesFolderId = isGoogle || isOneDrive;
  const rootCursor = usesFolderId ? "root" : "";
  const rootLabel = isGoogle ? "My Drive" : isOneDrive ? "OneDrive" : "Dropbox";

  const [open, setOpen] = useState(false);
  // breadcrumbs[last] = current level; breadcrumbs[0] = root
  const [breadcrumbs, setBreadcrumbs] = useState<{ cursor: string; label: string }[]>([
    { cursor: rootCursor, label: rootLabel },
  ]);

  const currentCursor = breadcrumbs[breadcrumbs.length - 1].cursor;

  const { data: folders, isLoading, isError, error } = useQuery({
    queryKey: [
      isGoogle ? "googleFolders" : isOneDrive ? "oneDriveFolders" : "dropboxFolders",
      businessId,
      currentCursor,
    ],
    queryFn: () => {
      if (isGoogle) return listGoogleFolders(businessId, currentCursor);
      if (isOneDrive) return listOneDriveFolders(businessId, currentCursor);
      return listDropboxFolders(businessId, currentCursor);
    },
    enabled: open,
    staleTime: 30_000,
  });

  function navigateInto(folder: StorageFolder) {
    setBreadcrumbs((b) => [...b, { cursor: folder.path_display, label: folder.name }]);
  }

  function navigateBack() {
    if (breadcrumbs.length <= 1) return;
    setBreadcrumbs((b) => b.slice(0, -1));
  }

  function selectFolder(folder: StorageFolder) {
    if (usesFolderId) {
      onChange(folder.name, folder.id);
    } else {
      onChange(folder.path_display);
    }
    setOpen(false);
    setBreadcrumbs([{ cursor: rootCursor, label: rootLabel }]);
  }

  function selectCurrentFolder() {
    const current = breadcrumbs[breadcrumbs.length - 1];
    if (usesFolderId) {
      onChange(current.label === rootLabel ? "" : current.label, current.cursor === rootCursor ? undefined : current.cursor);
    } else {
      onChange(current.cursor);
    }
    setOpen(false);
    setBreadcrumbs([{ cursor: rootCursor, label: rootLabel }]);
  }

  function clearSelection(e: React.MouseEvent) {
    e.stopPropagation();
    onChange("");
  }

  const atRoot = breadcrumbs.length === 1;
  const breadcrumbPath = breadcrumbs.map((b) => b.label).join(" / ");
  const folderErrorMessage = error instanceof Error ? error.message : `Check your ${isGoogle ? "Google Drive" : isOneDrive ? "OneDrive" : "Dropbox"} connection.`;

  return (
    <div className="relative">
      {/* Trigger field */}
      <div
        className="flex items-center gap-2 w-full px-3 py-2.5 rounded-lg cursor-pointer transition"
        style={{
          border: `1.5px solid ${BORDER}`,
          backgroundColor: SURFACE,
        }}
        onClick={() => setOpen((o) => !o)}
      >
        <FolderOpen size={14} style={{ color: NAVY, opacity: 0.4, flexShrink: 0 }} />
        <span className="flex-1 text-sm truncate" style={{ color: displayName ? NAVY : `${NAVY}55` }}>
          {displayName || `${rootLabel} root`}
        </span>
        {displayName && (
          <button
            type="button"
            onClick={clearSelection}
            className="p-0.5 rounded hover:opacity-70 transition"
            style={{ color: NAVY, opacity: 0.35 }}
          >
            <X size={12} />
          </button>
        )}
        <ChevronDown
          size={13}
          style={{
            color: NAVY,
            opacity: 0.35,
            flexShrink: 0,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 0.15s",
          }}
        />
      </div>

      {/* Dropdown panel */}
      {open && (
        <div
          className="absolute top-full mt-1.5 left-0 right-0 z-30 rounded-xl shadow-xl"
          style={{
            border: `1.5px solid ${NAVY}15`,
            backgroundColor: SURFACE,
            maxHeight: "260px",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Breadcrumb header */}
          <div
            className="flex items-center gap-2 px-3 py-2.5 flex-shrink-0"
            style={{ borderBottom: `1px solid ${NAVY}0d`, backgroundColor: `${NAVY}03` }}
          >
            {!atRoot && (
              <button
                type="button"
                onClick={navigateBack}
                className="flex items-center gap-1 text-xs font-semibold mr-1 transition-opacity hover:opacity-70"
                style={{ color: NAVY }}
              >
                <ArrowLeft size={12} />
                Back
              </button>
            )}
            {!atRoot && <ChevronRight size={10} style={{ color: NAVY, opacity: 0.3 }} />}
            <span className="text-xs truncate flex-1" style={{ color: NAVY, opacity: 0.55 }}>
              {breadcrumbPath}
            </span>
            <button
              type="button"
              onClick={selectCurrentFolder}
              className="ml-auto flex-shrink-0 text-xs font-semibold px-2 py-1 rounded-md transition-opacity hover:opacity-80"
              style={{ color: ORANGE, backgroundColor: SURFACE, border: `1px solid ${BORDER}` }}
            >
              {atRoot ? `Use ${rootLabel} root` : "Select this folder"}
            </button>
          </div>

          {/* Folder list */}
          <div className="overflow-y-auto flex-1">
            {isLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 size={18} className="animate-spin" style={{ color: ORANGE }} />
              </div>
            ) : isError ? (
              <p className="px-4 py-4 text-xs" style={{ color: UP }}>
                Could not load folders. {folderErrorMessage}
              </p>
            ) : !folders || folders.length === 0 ? (
              <p className="px-4 py-4 text-xs" style={{ color: NAVY, opacity: 0.5 }}>
                No subfolders here.
              </p>
            ) : (
              <ul>
                {folders.map((folder: StorageFolder, idx: number) => (
                  <li
                    key={folder.id}
                    className="flex items-center gap-3 px-3 py-2.5"
                    style={{ borderTop: idx > 0 ? `1px solid ${NAVY}08` : undefined }}
                  >
                    <FolderOpen size={14} style={{ color: ORANGE, flexShrink: 0 }} />
                    <span className="flex-1 text-sm truncate" style={{ color: NAVY }}>
                      {folder.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => selectFolder(folder)}
                      className="flex-shrink-0 text-xs font-semibold px-2 py-1 rounded-md transition-opacity hover:opacity-80"
                      style={{ color: ORANGE, backgroundColor: SURFACE, border: `1px solid ${BORDER}` }}
                    >
                      Select
                    </button>
                    <button
                      type="button"
                      onClick={() => navigateInto(folder)}
                      className="flex-shrink-0 p-1 rounded transition-opacity hover:opacity-70"
                      style={{ color: NAVY, opacity: 0.35 }}
                      title="Browse inside"
                    >
                      <ChevronRight size={13} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Settings form ─────────────────────────────────────────────────

interface SettingsFormProps {
  businessId?: string;
  initialProvider?: StorageProvider;
  initialSettings?: Pick<StorageIntegration, "autoExport" | "exportTemplate" | "dateSortStructure" | "rootFolderName" | "rootFolderId" | "deleteOnInvoiceDelete">;
  lockProvider?: boolean;
  onSave: (values: {
    provider: StorageProvider;
    autoExport: boolean;
    exportTemplate: ExportTemplate;
    dateSortStructure: DateSortStructure;
    rootFolderName: string;
    rootFolderId: string | null;
    deleteOnInvoiceDelete: boolean;
  }) => void;
  onCancel: () => void;
  isSaving: boolean;
  saveLabel?: string;
}

function SettingsForm({
  businessId,
  initialProvider = "google_drive",
  initialSettings,
  lockProvider = false,
  onSave,
  onCancel,
  isSaving,
  saveLabel = "Save & Connect",
}: SettingsFormProps) {
  const [provider, setProvider] = useState<StorageProvider>(initialProvider);
  const [autoExport, setAutoExport] = useState(initialSettings?.autoExport ?? false);
  const [deleteOnInvoiceDelete, setDeleteOnInvoiceDelete] = useState(initialSettings?.deleteOnInvoiceDelete ?? false);
  const [exportTemplate, setExportTemplate] = useState<ExportTemplate>(
    initialSettings?.exportTemplate ?? "single_folder",
  );
  const [dateSortStructure, setDateSortStructure] = useState<string>(
    initialSettings?.dateSortStructure ?? "year_month",
  );
  const dateParts = new Set(dateSortStructure.split("_"));

  function toggleDatePart(part: string) {
    const next = new Set(dateParts);
    if (next.has(part)) {
      if (next.size <= 1) return; // keep at least one active
      next.delete(part);
    } else {
      next.add(part);
    }
    setDateSortStructure(["year", "month", "week", "day"].filter((p) => next.has(p)).join("_"));
  }
  const [rootFolderName, setRootFolderName] = useState(initialSettings?.rootFolderName ?? "");
  const [rootFolderId, setRootFolderId] = useState<string | null>(initialSettings?.rootFolderId ?? null);

  function handleFolderChange(name: string, folderId?: string) {
    setRootFolderName(name);
    setRootFolderId(folderId ?? null);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSave({ provider, autoExport, exportTemplate, dateSortStructure, rootFolderName, rootFolderId, deleteOnInvoiceDelete });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">

      {/* Provider picker (only when not locked) */}
      {!lockProvider && (
        <div>
          <p className="text-xs font-bold mb-2" style={{ color: NAVY }}>Storage provider</p>
          <div className="grid grid-cols-3 gap-2">
            {PROVIDERS.map((p) => {
              const Icon = p.icon;
              const selected = provider === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setProvider(p.id)}
                  aria-pressed={selected}
                  className="flex flex-col items-center gap-1.5 py-3 px-2 rounded-lg transition-all"
                  style={{
                    border: `1.5px solid ${selected ? ACCENT_LINE : BORDER}`,
                    backgroundColor: selected ? ACCENT_WEAK : SURFACE,
                    boxShadow: selected ? `inset 0 0 0 1px ${ACCENT_LINE}` : undefined,
                  }}
                >
                  <Icon size={20} />
                  <span className="text-[11px] font-semibold" style={{ color: selected ? ORANGE : NAVY }}>{p.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Export template */}
      <div>
        <p className="text-xs font-bold mb-2" style={{ color: NAVY }}>Export template</p>
        <div className="grid grid-cols-2 gap-2">
          {(
            [
              {
                value: "single_folder" as ExportTemplate,
                label: "Single folder",
                description: "All invoices in one folder",
              },
              {
                value: "date_sorted" as ExportTemplate,
                label: "Date sorted",
                description: "Organised by year & month",
              },
            ] as const
          ).map((opt) => {
            const selected = exportTemplate === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setExportTemplate(opt.value)}
                aria-pressed={selected}
                className="flex flex-col items-start gap-1 py-3 px-3 rounded-lg transition-all text-left"
                style={{
                  border: `1.5px solid ${selected ? ACCENT_LINE : BORDER}`,
                  backgroundColor: selected ? ACCENT_WEAK : SURFACE,
                  boxShadow: selected ? `inset 0 0 0 1px ${ACCENT_LINE}` : undefined,
                }}
              >
                <span className="text-xs font-bold" style={{ color: selected ? ORANGE : NAVY }}>{opt.label}</span>
                <span className="text-[11px]" style={{ color: NAVY, opacity: selected ? 0.7 : 0.5 }}>{opt.description}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Date sort structure (only when date_sorted) */}
      {exportTemplate === "date_sorted" && (
        <div>
          <p className="text-xs font-bold mb-2" style={{ color: NAVY }}>Date folder structure</p>
          <div className="grid grid-cols-4 gap-2">
            {(["year", "month", "week", "day"] as const).map((part) => {
              const active = dateParts.has(part);
              const isLast = active && dateParts.size === 1;
              return (
                <button
                  key={part}
                  type="button"
                  disabled={isLast}
                  onClick={() => toggleDatePart(part)}
                  aria-pressed={active}
                  className="flex flex-col items-center gap-0.5 py-2.5 px-2 rounded-lg transition-all disabled:cursor-not-allowed"
                  style={{
                    border: `1.5px solid ${active ? ACCENT_LINE : BORDER}`,
                    backgroundColor: active ? ACCENT_WEAK : SURFACE,
                    boxShadow: active ? `inset 0 0 0 1px ${ACCENT_LINE}` : undefined,
                  }}
                >
                  <span className="text-xs font-bold capitalize" style={{ color: active ? ORANGE : NAVY }}>{part}</span>
                  <span className="text-[10px]" style={{ color: NAVY, opacity: active ? 0.68 : 0.45 }}>
                    {part === "year" ? "2026" : part === "month" ? "Apr" : part === "week" ? "Wk 1–4" : "Day 29"}
                  </span>
                </button>
              );
            })}
          </div>
          <p className="text-[11px] mt-1.5" style={{ color: NAVY, opacity: 0.45 }}>
            {["year", "month", "week", "day"]
              .filter((p) => dateParts.has(p))
              .map((p) => (p === "year" ? "2026" : p === "month" ? "04 - April" : p === "week" ? (dateParts.has("month") ? "Week 1–4" : "Week 1–52") : "29"))
              .join(" / ")}
          </p>
        </div>
      )}

      {/* Root folder */}
      <div>
        <label className="block text-xs font-bold mb-1.5" style={{ color: NAVY }}>
          Root destination folder
        </label>
        <FolderPickerField
          provider={provider}
          businessId={businessId}
          displayName={rootFolderName}
          onChange={handleFolderChange}
        />
        <p className="text-[11px] mt-1" style={{ color: NAVY, opacity: 0.45 }}>
          The app will create and manage subfolders under this root automatically.
        </p>
      </div>

      {/* Folder structure preview */}
      <FolderPreview
        template={exportTemplate}
        dateSortStructure={dateSortStructure}
        rootFolderName={rootFolderName}
      />

      {/* Auto export toggle */}
      <div
        className="flex items-center justify-between px-4 py-3 rounded-lg"
        style={{ backgroundColor: `${NAVY}05`, border: `1.5px solid ${NAVY}10` }}
      >
        <div>
          <p className="text-xs font-bold" style={{ color: NAVY }}>Auto export</p>
          <p className="text-[11px] mt-0.5" style={{ color: NAVY, opacity: 0.5 }}>
            Automatically export every new invoice to this storage
          </p>
        </div>
        <button
          type="button"
          onClick={() => setAutoExport((v) => !v)}
          className="flex-shrink-0 ml-4 transition-opacity hover:opacity-80"
        >
          {autoExport ? (
            <ToggleRight size={26} style={{ color: ORANGE }} />
          ) : (
            <ToggleLeft size={26} style={{ color: NAVY, opacity: 0.3 }} />
          )}
        </button>
      </div>

      {/* Delete on invoice delete toggle */}
      <div
        className="flex items-center justify-between px-4 py-3 rounded-lg"
        style={{ backgroundColor: `${NAVY}05`, border: `1.5px solid ${NAVY}10` }}
      >
        <div>
          <p className="text-xs font-bold" style={{ color: NAVY }}>Delete from storage on invoice delete</p>
          <p className="text-[11px] mt-0.5" style={{ color: NAVY, opacity: 0.5 }}>
            When you delete an invoice, also remove its file from this storage
          </p>
        </div>
        <button
          type="button"
          onClick={() => setDeleteOnInvoiceDelete((v) => !v)}
          className="flex-shrink-0 ml-4 transition-opacity hover:opacity-80"
        >
          {deleteOnInvoiceDelete ? (
            <ToggleRight size={26} style={{ color: ORANGE }} />
          ) : (
            <ToggleLeft size={26} style={{ color: NAVY, opacity: 0.3 }} />
          )}
        </button>
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          disabled={isSaving}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold text-white transition-opacity disabled:opacity-60"
          style={{ backgroundColor: ORANGE }}
        >
          {isSaving && <Loader2 size={14} className="animate-spin" />}
          {saveLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSaving}
          className="px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
          style={{ color: NAVY, backgroundColor: `${NAVY}0a` }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ── Main page ──────────────────────────────────────────────────────

type PageMode = "view" | "connect" | "edit" | "confirm_disconnect";
type IntegrationTarget = { businessId: string; provider: StorageProvider };

function targetKey(target: IntegrationTarget | null) {
  return target ? `${target.businessId}:${target.provider}` : "";
}

export default function IntegrationsPage({ embedded = false }: { embedded?: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { memberships, activeBusiness, loading: businessesLoading } = useBusiness();
  const queryClient = useQueryClient();

  const [mode, setMode] = useState<PageMode>("view");
  const [selectedTarget, setSelectedTarget] = useState<IntegrationTarget | null>(null);
  const [connectingTarget, setConnectingTarget] = useState<IntegrationTarget | null>(null);
  const [postOAuth, setPostOAuth] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const businessIds = activeBusiness ? [activeBusiness.id] : [];

  // Handle ?oauth=provider or ?error= params returned from the OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const oauth = params.get("oauth");
    const error = params.get("error");
    const oauthBusinessId = params.get("businessId");
    if (oauth) {
      setPostOAuth(true);
      setMode("edit"); // opens settings form once integration data loads
      setSelectedTarget({
        businessId: oauthBusinessId || "",
        provider: oauth as StorageProvider,
      });
      queryClient.invalidateQueries({ queryKey: ["storageIntegrationsByBusiness"] });
      window.history.replaceState({}, "", "/integrations");
    }
    if (error) {
      setErrorMsg(decodeURIComponent(error));
      window.history.replaceState({}, "", "/integrations");
    }
  }, []); // only run on mount

  const { data: storageByBusinessData, isLoading } = useQuery<Record<string, StorageIntegration[]>>({
    queryKey: ["storageIntegrationsByBusiness", businessIds.join("|")],
    queryFn: async () => {
      if (!activeBusiness) return {};
      const integrations = await fetchStorageIntegrations(activeBusiness.id);
      return { [activeBusiness.id]: integrations } as Record<string, StorageIntegration[]>;
    },
    enabled: Boolean(activeBusiness),
  });
  const storageByBusiness: Record<string, StorageIntegration[]> = storageByBusinessData ?? {};

  const updateMutation = useMutation({
    mutationFn: ({
      target,
      values,
      creating,
    }: {
      target: IntegrationTarget;
      values: {
        autoExport: boolean;
        exportTemplate: ExportTemplate;
        dateSortStructure: DateSortStructure;
        rootFolderName: string;
        rootFolderId: string | null;
        deleteOnInvoiceDelete: boolean;
      };
      creating: boolean;
    }) => {
      const payload = {
        autoExport: values.autoExport,
        exportTemplate: values.exportTemplate,
        dateSortStructure: values.dateSortStructure,
        rootFolderName: values.rootFolderName || null,
        rootFolderId: values.rootFolderId || null,
        deleteOnInvoiceDelete: values.deleteOnInvoiceDelete,
      };
      return creating
        ? connectStorageIntegration({ provider: target.provider, ...payload }, target.businessId)
        : updateStorageIntegration(target.provider, payload, target.businessId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["storageIntegrationsByBusiness"] });
      setMode("view");
      setSelectedTarget(null);
      setErrorMsg(null);
      if (postOAuth) {
        setSuccessMsg("Storage connected and export settings saved.");
        setPostOAuth(false);
      } else {
        setSuccessMsg("Storage settings saved.");
      }
    },
    onError: (err: Error) => setErrorMsg(err.message),
  });

  const disconnectMutation = useMutation({
    mutationFn: (target: IntegrationTarget) =>
      disconnectStorageIntegration(target.provider, target.businessId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["storageIntegrationsByBusiness"] });
      setMode("view");
      setSelectedTarget(null);
      setErrorMsg(null);
      setSuccessMsg("Storage connection removed.");
      setPostOAuth(false);
    },
    onError: (err: Error) => setErrorMsg(err.message),
  });

  // Click Connect on a provider card — redirect straight to OAuth for Dropbox
  async function handleProviderConnect(businessId: string, provider: StorageProvider) {
    if (provider === "onedrive") {
      setErrorMsg(null);
      setSuccessMsg("OneDrive is coming soon.");
      return;
    }

    const target = { businessId, provider };
    setErrorMsg(null);
    setSuccessMsg(null);
    setConnectingTarget(target);
    try {
      if (provider === "dropbox") {
        const { url } = await getDropboxAuthUrl(businessId);
        window.location.href = url;
      } else if (provider === "google_drive") {
        const { url } = await getGoogleAuthUrl(businessId);
        window.location.href = url;
      } else if (provider === "onedrive") {
        const { url } = await getOneDriveAuthUrl(businessId);
        window.location.href = url;
      } else {
        // Other providers: show settings form first
        setSelectedTarget(target);
        setMode("connect");
        setConnectingTarget(null);
      }
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Failed to start connection.");
      setConnectingTarget(null);
    }
  }

  // Save settings (used for both the post-OAuth setup and editing an existing integration)
  function handleUpdate(values: {
    provider: StorageProvider;
    autoExport: boolean;
    exportTemplate: ExportTemplate;
    dateSortStructure: DateSortStructure;
    rootFolderName: string;
    rootFolderId: string | null;
    deleteOnInvoiceDelete: boolean;
  }) {
    if (!selectedTarget) return;
    setErrorMsg(null);
    const existing = storageByBusiness[selectedTarget.businessId]?.some(
      (integration) => integration.provider === selectedTarget.provider,
    );
    updateMutation.mutate({
      target: selectedTarget,
      values,
      creating: !existing,
    });
  }

  const isMutating = updateMutation.isPending || disconnectMutation.isPending;
  const selectedBusiness = memberships.find((business) => business.id === selectedTarget?.businessId);
  const selectedIntegration = selectedTarget
    ? storageByBusiness[selectedTarget.businessId]?.find((integration) => integration.provider === selectedTarget.provider)
    : null;

  return (
    <div
      className={embedded ? "py-8 px-4" : "min-h-screen py-8 px-4"}
      style={{
        backgroundColor: embedded ? "transparent" : CANVAS,
        fontFamily: "var(--font-ui)",
      }}
    >
      <div className="w-full max-w-6xl mx-auto">

        {/* ── Page header ───────────────────────────────────────── */}
        {!embedded && (
          <div className="mb-8">
            <button
              onClick={() => navigate("/console-settings")}
              className="flex items-center gap-1.5 text-sm mb-5 transition-opacity hover:opacity-70"
              style={{ color: NAVY, opacity: 0.55 }}
            >
              <ArrowLeft size={16} />
              Back to settings
            </button>

            <div className="flex items-start gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: ACCENT_WEAK, color: ORANGE, border: `1px solid ${ACCENT_LINE}` }}
              >
                <Plug size={22} />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-[27px] font-semibold leading-tight" style={{ color: NAVY }}>Integrations</h1>
                </div>
                <p className="text-sm mt-1" style={{ color: MUTED }}>
                  Connect external services for each business and decide where each invoice copy should go.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ── Success banner ────────────────────────────────────── */}
        {successMsg && (
          <div
            className="mb-5 flex items-center gap-3 px-4 py-3 rounded-lg text-sm"
            style={{
              backgroundColor: DOWN_WEAK,
              border: `1px solid ${DOWN_LINE}`,
              color: DOWN,
            }}
          >
            <CheckCircle2 size={15} className="flex-shrink-0" />
            {successMsg}
          </div>
        )}

        {/* ── Error banner ──────────────────────────────────────── */}
        {errorMsg && (
          <div
            className="mb-5 flex items-start gap-3 px-4 py-3 rounded-lg text-sm"
            style={{
              backgroundColor: UP_WEAK,
              border: `1px solid ${UP_LINE}`,
              color: UP,
            }}
          >
            <AlertTriangle size={15} className="flex-shrink-0 mt-0.5" />
            {errorMsg}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════ */}
        {/* SECTION 1 — STORAGE INTEGRATIONS                      */}
        {/* ══════════════════════════════════════════════════════ */}

        <section className="mb-8">
          {/* Section header */}
          <div className="flex items-center gap-2 mb-1">
            <HardDrive size={16} style={{ color: ORANGE }} />
            <h2 className="text-base font-semibold" style={{ color: NAVY }}>Storage Integrations</h2>
          </div>
          {businessesLoading || isLoading ? (
            <div className="flex items-center justify-center p-10 rounded-2xl" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, boxShadow: SHADOW }}>
              <Loader2 size={22} className="animate-spin" style={{ color: ORANGE }} />
            </div>
          ) : !activeBusiness ? (
            <div className="px-5 py-5 rounded-2xl" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, boxShadow: SHADOW }}>
              <p className="text-sm font-semibold" style={{ color: NAVY }}>No active business</p>
              <p className="text-xs mt-1" style={{ color: MUTED }}>
                Create or join a business before connecting storage platforms.
              </p>
            </div>
          ) : (
            <BusinessStorageSection
              business={activeBusiness}
              integrations={Array.isArray(storageByBusiness[activeBusiness.id]) ? storageByBusiness[activeBusiness.id] : []}
              connectingTargetKey={targetKey(connectingTarget)}
              onConnect={handleProviderConnect}
              onEdit={(provider) => {
                setSelectedTarget({ businessId: activeBusiness.id, provider });
                setMode("edit");
                setErrorMsg(null);
                setSuccessMsg(null);
              }}
              onDisconnect={(provider) => {
                setSelectedTarget({ businessId: activeBusiness.id, provider });
                setMode("confirm_disconnect");
                setErrorMsg(null);
                setSuccessMsg(null);
              }}
            />
          )}
        </section>

        {/* ══════════════════════════════════════════════════════ */}
        {/* SECTION 2 — SUPPLY ORDER INTEGRATIONS (COMING SOON)   */}
        {/* ══════════════════════════════════════════════════════ */}

        <section>
          {/* Section header */}
          <div className="flex items-center gap-2 mb-1">
            <ShoppingCart size={16} style={{ color: NAVY, opacity: 0.35 }} />
            <h2 className="text-base font-semibold" style={{ color: MUTED }}>
              Supply Order Integrations
            </h2>
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide"
              style={{ backgroundColor: CHIP, color: MUTED, border: `1px solid ${LINE}` }}
            >
              Coming Soon
            </span>
          </div>
          <p className="text-sm mb-5" style={{ color: MUTED }}>
            Connect directly to supplier ordering platforms to submit and track orders from within
            Bill Lens. This feature is on our product roadmap and will be available in a future update.
          </p>

          <div
            className="rounded-2xl overflow-hidden"
            style={{
              backgroundColor: SURFACE,
              border: `1px solid ${LINE}`,
              boxShadow: SHADOW,
              opacity: 0.7,
            }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x"
              style={{ borderColor: `${NAVY}0e` }}>
              {SUPPLY_ORDER_PLACEHOLDERS.map((item) => (
                <SupplyOrderPlaceholder key={item.label} {...item} />
              ))}
            </div>

            <div
              className="px-6 py-4 flex items-center gap-3"
              style={{ borderTop: `1px solid ${NAVY}0d`, backgroundColor: `${NAVY}03` }}
            >
              <Clock size={13} style={{ color: NAVY, opacity: 0.3 }} />
              <p className="text-xs" style={{ color: NAVY, opacity: 0.4 }}>
                Supply order integrations are planned for a future release. Stay tuned for updates.
              </p>
            </div>
          </div>
        </section>

      </div>

      {(mode === "connect" || mode === "edit") && selectedTarget && (
        <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto px-4 py-8" style={{ backgroundColor: "rgba(80,55,30,0.35)" }}>
          <div className="w-full max-w-xl rounded-xl bg-white shadow-xl" style={{ border: `1px solid ${NAVY}18` }}>
            <div className="px-5 py-4 flex items-start justify-between" style={{ borderBottom: `1px solid ${NAVY}0d` }}>
              <div>
                <p className="text-sm font-bold" style={{ color: NAVY }}>
                  {mode === "connect" ? "Connect" : "Edit"} {getProvider(selectedTarget.provider).name}
                </p>
                <p className="text-xs mt-0.5" style={{ color: NAVY, opacity: 0.5 }}>
                  {selectedBusiness?.name || "Selected business"}
                </p>
              </div>
              <button
                onClick={() => { setMode("view"); setSelectedTarget(null); setErrorMsg(null); setPostOAuth(false); }}
                className="rounded-lg p-1.5 transition-colors hover:opacity-70 flex-shrink-0 ml-4"
                style={{ color: NAVY, opacity: 0.4, backgroundColor: `${NAVY}08` }}
                aria-label="Close"
              >
                <X size={15} />
              </button>
            </div>
            <div className="p-5">
              {postOAuth && (
                <div className="flex items-start gap-3 mb-5 px-4 py-3 rounded-lg"
                  style={{ backgroundColor: "rgba(16,185,129,0.07)", border: "1px solid rgba(16,185,129,0.2)" }}>
                  <CheckCircle2 size={15} style={{ color: "#059669", flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <p className="text-sm font-bold" style={{ color: "#065f46" }}>{getProvider(selectedTarget.provider).name} connected!</p>
                    <p className="text-xs mt-0.5" style={{ color: "#065f46", opacity: 0.8 }}>
                      Now choose how and where to export invoice files for this business.
                    </p>
                  </div>
                </div>
              )}
              <SettingsForm
                businessId={selectedTarget.businessId}
                initialProvider={selectedTarget.provider}
                initialSettings={selectedIntegration || undefined}
                lockProvider
                onSave={handleUpdate}
                onCancel={() => { setMode("view"); setSelectedTarget(null); setErrorMsg(null); setPostOAuth(false); }}
                isSaving={updateMutation.isPending}
                saveLabel={mode === "connect" || postOAuth ? "Save connection" : "Save changes"}
              />
            </div>
          </div>
        </div>
      )}

      {mode === "confirm_disconnect" && selectedTarget && selectedIntegration && (
        <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto px-4 py-8" style={{ backgroundColor: "rgba(80,55,30,0.35)" }}>
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl" style={{ border: `1px solid ${NAVY}18` }}>
            <div className="flex items-start gap-3">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: UP_WEAK }}
              >
                <Link2Off size={16} style={{ color: UP }} />
              </div>
              <div className="flex-1">
                <p className="text-sm font-bold mb-1" style={{ color: NAVY }}>
                  Disconnect {getProvider(selectedIntegration.provider).name}?
                </p>
                <p className="text-sm mb-4" style={{ color: NAVY, opacity: 0.55 }}>
                  Export settings will be removed for {selectedBusiness?.name || "this business"}.
                  Previously exported files will remain in storage.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => disconnectMutation.mutate(selectedTarget)}
                    disabled={isMutating}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold text-white transition-opacity disabled:opacity-60"
                    style={{ backgroundColor: UP }}
                  >
                    {disconnectMutation.isPending && <Loader2 size={13} className="animate-spin" />}
                    Disconnect
                  </button>
                  <button
                    onClick={() => { setMode("view"); setSelectedTarget(null); }}
                    disabled={isMutating}
                    className="px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
                    style={{ color: NAVY, backgroundColor: `${NAVY}0a` }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Business storage section ──────────────────────────────────────

function BusinessStorageSection({
  business,
  integrations,
  connectingTargetKey,
  onConnect,
  onEdit,
  onDisconnect,
}: {
  business: { id: string; name: string; role: string };
  integrations: StorageIntegration[];
  connectingTargetKey: string;
  onConnect: (businessId: string, provider: StorageProvider) => void;
  onEdit: (provider: StorageProvider) => void;
  onDisconnect: (provider: StorageProvider) => void;
}) {
  const safeIntegrations = Array.isArray(integrations) ? integrations : [];
  const integrationByProvider = new Map(safeIntegrations.map((integration) => [integration.provider, integration]));

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        backgroundColor: SURFACE,
        border: `1px solid ${LINE}`,
        boxShadow: SHADOW,
      }}
    >
      <div className="px-5 py-4 flex items-center justify-between gap-3" style={{ backgroundColor: RAISED, borderBottom: `1px solid ${LINE}` }}>
        <div>
          <p className="text-sm font-semibold" style={{ color: NAVY }}>{business.name}</p>
          <p className="text-xs mt-0.5" style={{ color: MUTED }}>
            {safeIntegrations.length} connected storage {safeIntegrations.length === 1 ? "platform" : "platforms"}
          </p>
        </div>
        <span
          className="text-[11px] font-semibold px-2 py-0.5 rounded-full capitalize"
          style={{ backgroundColor: CHIP, color: NAVY, border: `1px solid ${LINE}` }}
        >
          {business.role}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x" style={{ borderColor: LINE }}>
        {PROVIDERS.map((provider) => {
          const integration = integrationByProvider.get(provider.id);
          const key = `${business.id}:${provider.id}`;
          return integration ? (
            <ConnectedCard
              key={provider.id}
              integration={integration}
              compact
              onEdit={() => onEdit(provider.id)}
              onDisconnect={() => onDisconnect(provider.id)}
            />
          ) : (
            <ProviderConnectTile
              key={provider.id}
              businessId={business.id}
              provider={provider}
              isConnecting={connectingTargetKey === key}
              isBusy={Boolean(connectingTargetKey)}
              onConnect={onConnect}
            />
          );
        })}
      </div>
    </div>
  );
}

// ── Connected card ─────────────────────────────────────────────────

function ConnectedCard({
  integration,
  compact = false,
  onEdit,
  onDisconnect,
}: {
  integration: StorageIntegration;
  compact?: boolean;
  onEdit: () => void;
  onDisconnect: () => void;
}) {
  const provider = getProvider(integration.provider);
  const Icon = provider.icon;

  const templateLabel =
    integration.exportTemplate === "single_folder"
      ? "Single folder"
      : integration.dateSortStructure === "year_month_week"
      ? "Date sorted — Year / Month / Week"
      : "Date sorted — Year / Month";

  return (
    <div className={compact ? "p-5" : "p-6"}>
      {/* Provider + status row */}
      <div className="flex items-start gap-4 mb-5">
        <div
          className={`${compact ? "w-10 h-10" : "w-12 h-12"} rounded-xl flex items-center justify-center flex-shrink-0`}
          style={{ backgroundColor: provider.bgColor }}
        >
          <Icon size={compact ? 20 : 22} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-bold" style={{ color: NAVY }}>{provider.name}</p>
            <span
              className="flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full"
              style={{ backgroundColor: "rgba(16,185,129,0.1)", color: "#065f46" }}
            >
              <CheckCircle2 size={10} />
              Connected
            </span>
          </div>
          {integration.lastExportAt && (
            <p className="text-xs mt-0.5 flex items-center gap-1" style={{ color: NAVY, opacity: 0.45 }}>
              <RefreshCw size={10} />
              Last export: {new Date(integration.lastExportAt).toLocaleDateString("en-AU", {
                day: "numeric", month: "short", year: "numeric",
              })}
              {integration.lastExportStatus === "failed" && (
                <span className="text-red-500 font-semibold ml-1">— failed</span>
              )}
            </p>
          )}
          {!integration.lastExportAt && (
            <p className="text-xs mt-0.5" style={{ color: NAVY, opacity: 0.35 }}>
              No exports yet
            </p>
          )}
        </div>
      </div>

      {/* Settings summary */}
      {!compact && (
        <div
          className="rounded-xl divide-y mb-5"
          style={{
            border: `1.5px solid ${NAVY}10`,
          }}
        >
          {[
            {
              label: "Auto export",
              value: integration.autoExport ? "Enabled" : "Disabled",
              valueColor: integration.autoExport ? "#065f46" : (NAVY as string),
              valueBg: integration.autoExport ? "rgba(16,185,129,0.08)" : "transparent",
            },
            {
              label: "Export template",
              value: templateLabel,
              valueColor: NAVY as string,
              valueBg: "transparent",
            },
            {
              label: "Root folder",
              value: integration.rootFolderName || "Bill Lens Invoices (default)",
              valueColor: NAVY as string,
              valueBg: "transparent",
            },
            {
              label: "Delete from storage on invoice delete",
              value: integration.deleteOnInvoiceDelete ? "Enabled" : "Disabled",
              valueColor: integration.deleteOnInvoiceDelete ? "#065f46" : (NAVY as string),
              valueBg: integration.deleteOnInvoiceDelete ? "rgba(16,185,129,0.08)" : "transparent",
            },
          ].map(({ label, value, valueColor, valueBg }, i) => (
            <div
              key={label}
              className="flex items-center justify-between gap-3 px-4 py-3"
              style={{ borderTop: i > 0 ? `1px solid ${NAVY}08` : undefined }}
            >
              <p className="text-xs" style={{ color: NAVY, opacity: 0.5 }}>{label}</p>
              <span
                className="text-xs font-semibold px-2 py-0.5 rounded-full"
                style={{ color: valueColor, backgroundColor: valueBg }}
              >
                {value}
              </span>
            </div>
          ))}
        </div>
      )}

      {compact && (
        <div className="space-y-1.5 mb-5">
          <p className="text-xs" style={{ color: NAVY, opacity: 0.55 }}>
            {integration.autoExport ? "Auto export enabled" : "Auto export disabled"}
          </p>
          <p className="text-xs" style={{ color: NAVY, opacity: 0.55 }}>
            {templateLabel}
          </p>
          <p className="text-xs truncate" style={{ color: NAVY, opacity: 0.55 }}>
            {integration.rootFolderName || "Bill Lens Invoices (default)"}
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2">
        <button
          onClick={onEdit}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors"
          style={{ backgroundColor: `${NAVY}0a`, color: NAVY }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = `${NAVY}15`)}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = `${NAVY}0a`)}
        >
          <Pencil size={12} />
          Edit settings
        </button>
        <button
          onClick={onDisconnect}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors"
          style={{ color: UP, backgroundColor: "rgba(220,38,38,0.07)" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(220,38,38,0.12)")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "rgba(220,38,38,0.07)")}
        >
          <Link2Off size={12} />
          Disconnect
        </button>
      </div>
    </div>
  );
}

// ── Provider picker (no integration yet) ─────────────────────────

function ProviderConnectTile({
  businessId,
  provider,
  isConnecting,
  isBusy,
  onConnect,
}: {
  businessId: string;
  provider: ProviderMeta;
  isConnecting: boolean;
  isBusy: boolean;
  onConnect: (businessId: string, provider: StorageProvider) => void;
}) {
  const Icon = provider.icon;
  const comingSoon = Boolean(provider.comingSoon);

  return (
    <div className="px-5 py-5" style={{ opacity: comingSoon ? 0.72 : 1 }}>
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
        style={{ backgroundColor: provider.bgColor }}
      >
        <Icon size={20} />
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm font-semibold" style={{ color: NAVY }}>{provider.name}</p>
        {comingSoon && (
          <span
            className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide"
            style={{ backgroundColor: `${NAVY}10`, color: NAVY, opacity: 0.75 }}
          >
            Coming soon
          </span>
        )}
      </div>
      <p className="text-xs mt-1 mb-4" style={{ color: NAVY, opacity: 0.45 }}>
        {provider.description}
      </p>
      <button
        onClick={() => onConnect(businessId, provider.id)}
        disabled={isBusy || comingSoon}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-opacity disabled:opacity-50"
        style={{
          backgroundColor: comingSoon ? `${NAVY}12` : ORANGE,
          color: comingSoon ? NAVY : "#f0ebe2",
        }}
      >
        {comingSoon
          ? <><Clock size={12} />Coming soon</>
          : isConnecting
          ? <><Loader2 size={12} className="animate-spin" />Connecting…</>
          : <><Link2 size={12} />Connect</>
        }
      </button>
    </div>
  );
}

// ── Supply order placeholders ─────────────────────────────────────

const SUPPLY_ORDER_PLACEHOLDERS: { label: string; description: string; icon: LucideIcon }[] = [
  {
    label: "Supplier Ordering Hubs",
    description: "Connect to centralised supplier ordering platforms to place and track orders directly.",
    icon: ShoppingCart,
  },
  {
    label: "Direct Supplier Portals",
    description: "Integrate with individual supplier portals for streamlined order submission.",
    icon: Link2,
  },
  {
    label: "Order Export & Sync",
    description: "Push orders to your supplier systems automatically from your par level checks.",
    icon: RefreshCw,
  },
];

function SupplyOrderPlaceholder({
  label,
  description,
  icon: Icon,
}: {
  label: string;
  description: string;
  icon: LucideIcon;
}) {
  return (
    <div className="px-5 py-5">
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center mb-3"
        style={{ backgroundColor: `${NAVY}0a` }}
      >
        <Icon size={15} style={{ color: NAVY, opacity: 0.35 }} />
      </div>
      <p className="text-xs font-bold mb-1" style={{ color: NAVY, opacity: 0.45 }}>
        {label}
      </p>
      <p className="text-[11px] leading-relaxed" style={{ color: NAVY, opacity: 0.35 }}>
        {description}
      </p>
    </div>
  );
}
